import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { ConfirmModal, AppButton } from '../ui';
import { CalendarPlus, Trash2 } from "lucide-react";

const CalendarEventModal = ({ show, onHide, event, defaultDate, onSave, onDelete, saving }) => {
  const isEditing = !!event;

  const toInputDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
  };

  const [title, setTitle] = useState('');
  const [type, setType] = useState('HOLIDAY');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('10:00');
  const [isWorkingDay, setIsWorkingDay] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (show) {
      if (event) {
        setTitle(event.title || '');
        setType(event.type || 'HOLIDAY');
        setStartDate(toInputDate(event.startDate));
        setEndDate(toInputDate(event.endDate));
        setAllDay(event.allDay !== false);
        setStartTime(event.startTime || '09:00');
        setEndTime(event.endTime || '10:00');
        setIsWorkingDay(event.isWorkingDay ?? false);
      } else {
        setTitle('');
        setType('HOLIDAY');
        setStartDate(defaultDate ? toInputDate(defaultDate) : '');
        setEndDate(defaultDate ? toInputDate(defaultDate) : '');
        setAllDay(true);
        setStartTime('09:00');
        setEndTime('10:00');
        setIsWorkingDay(false);
      }
      setDeleteLoading(false);
    }
  }, [show, event, defaultDate]);

  // HOLIDAYs must always be all-day and non-working
  const effectiveAllDay = type === 'HOLIDAY' ? true : allDay;
  const effectiveIsWorkingDay = type === 'HOLIDAY' ? false : isWorkingDay;

  const isValid = title.trim() && startDate && endDate && endDate >= startDate
    && (effectiveAllDay || (startTime && endTime && endTime > startTime));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSave({
      ...(event && { _id: event._id }),
      title: title.trim(),
      type,
      startDate,
      endDate: effectiveAllDay ? endDate : startDate, // timed events are single-day
      allDay: effectiveAllDay,
      startTime: effectiveAllDay ? null : startTime,
      endTime: effectiveAllDay ? null : endTime,
      isWorkingDay: effectiveIsWorkingDay,
    });
  };

  const handleDelete = () => setShowDeleteConfirm(true);

  const confirmDelete = async () => {
    setShowDeleteConfirm(false);
    setDeleteLoading(true);
    await onDelete(event._id);
    setDeleteLoading(false);
  };

  return (
  <>
    <Modal show={show} onHide={onHide} centered className="ic-event-modal" backdrop="static">
      <Modal.Header closeButton className="ic-modal-header">
        <Modal.Title className="ic-modal-title">
          <CalendarPlus size={16} />
          {isEditing ? 'Edit Calendar Event' : 'Add Calendar Event'}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="ic-modal-body">
          {/* Title */}
          <div className="ic-modal-field">
            <label>Title</label>
            <Form.Control
              type="text"
              placeholder="e.g. Republic Day, Team Standup"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          {/* Type */}
          <div className="ic-modal-field">
            <label>Type</label>
            <Form.Select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="HOLIDAY">Holiday</option>
              <option value="EVENT">Event</option>
            </Form.Select>
          </div>

          {/* All Day toggle (only for EVENT type) */}
          {type === 'EVENT' && (
            <div className="ic-modal-field">
              <label className="ic-modal-working-toggle">
                <Form.Check
                  type="switch"
                  checked={allDay}
                  onChange={(e) => setAllDay(e.target.checked)}
                  id="event-allday-switch"
                />
                All day event
              </label>
            </div>
          )}

          {/* Date Range (all-day) or single Date + Time (timed) */}
          {effectiveAllDay ? (
            <div className="ic-modal-dates">
              <div className="ic-modal-field">
                <label>Start Date</label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div className="ic-modal-field">
                <label>End Date</label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  required
                />
              </div>
            </div>
          ) : (
            <>
              <div className="ic-modal-field">
                <label>Date</label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div className="ic-modal-dates">
                <div className="ic-modal-field">
                  <label>Start Time</label>
                  <Form.Control
                    type="time"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    required
                  />
                </div>
                <div className="ic-modal-field">
                  <label>End Time</label>
                  <Form.Control
                    type="time"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    min={startTime}
                    required
                  />
                </div>
              </div>
            </>
          )}

          {/* Is Working Day (only for EVENT type) */}
          {type === 'EVENT' && effectiveAllDay && (
            <div className="ic-modal-field">
              <label className="ic-modal-working-toggle">
                <Form.Check
                  type="switch"
                  checked={isWorkingDay}
                  onChange={(e) => setIsWorkingDay(e.target.checked)}
                  id="event-working-day-switch"
                />
                Mark as working day override
              </label>
            </div>
          )}

          {/* Validation errors */}
          {startDate && endDate && endDate < startDate && effectiveAllDay && (
            <div className="text-danger" style={{ fontSize: '0.8125rem' }}>
              End date cannot be before start date.
            </div>
          )}
          {!effectiveAllDay && startTime && endTime && endTime <= startTime && (
            <div className="text-danger" style={{ fontSize: '0.8125rem' }}>
              End time must be after start time.
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className="ic-modal-footer">
          <div className="ic-modal-footer-left">
            {isEditing && (
              <Button
                variant="outline-danger"
                size="sm"
                className="ic-modal-btn"
                onClick={handleDelete}
                disabled={deleteLoading || saving}
              >
                {deleteLoading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <><Trash2 size={11} /> Delete</>
                )}
              </Button>
            )}
          </div>
          <div className="ic-modal-footer-right">
            <Button
              variant="outline-secondary"
              size="sm"
              className="ic-modal-btn"
              onClick={onHide}
              disabled={saving}
            >
              Cancel
            </Button>
            <AppButton
              size="sm"
              className="ic-modal-btn"
              type="submit"
              disabled={!isValid || saving}
            >
              {saving ? <Spinner animation="border" size="sm" /> : isEditing ? 'Update' : 'Save'}
            </AppButton>
          </div>
        </Modal.Footer>
      </Form>
    </Modal>

    <ConfirmModal
      show={showDeleteConfirm}
      onHide={() => setShowDeleteConfirm(false)}
      onConfirm={confirmDelete}
      title="Delete Event"
      message={`Delete "${event?.title}"? This cannot be undone.`}
      confirmText="Delete"
      variant="danger"
    />
  </>
  );
};

export default CalendarEventModal;
